'use client';

import { useEffect, useState, useCallback, memo, useRef } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { tmdb } from '@/lib/tmdb';
import { useStore } from '@/lib/store';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Search,
  TrendingUp,
  Star,
  Film,
  Tv,
  PlayCircle,
  Calendar,
  ChevronRight,
  Sparkles,
  Clock,
  ArrowRight,
  Info,
  Plus
} from 'lucide-react';
import Image from 'next/image';
import { MediaCard as ReusableMediaCard } from '@/components/MediaCard';
import { PersonalizedRow } from '@/components/PersonalizedRow';
import { DynamicPersonalizedRows } from '@/components/DynamicPersonalizedRows';
import { detectUserLanguages } from '@/lib/language-preferences';
import { Header } from '@/components/Header';
import { getActiveSeasonalPeriod, SeasonalPeriod, shuffleWithSeed, getDailySeed } from '@/lib/seasonal-movies';
import { useTheme } from '@/contexts/ThemeContext';
import { CategoryBrowser } from '@/components/CategoryBrowser';

interface MediaItem {
  id: number;
  title?: string;
  name?: string;
  poster_path: string | null;
  backdrop_path: string | null;
  vote_average: number;
  release_date?: string;
  first_air_date?: string;
  media_type?: string;
  overview?: string;
}

export default function Home() {
  const { user } = useAuth();
  const router = useRouter();
  const { addToWatchlist, watchlist } = useStore();
  const { activeTheme, loading: themeLoading } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<MediaItem[]>([]);
  const [trending, setTrending] = useState<MediaItem[]>([]);
  const [popularShows, setPopularShows] = useState<MediaItem[]>([]);
  const [popularMovies, setPopularMovies] = useState<MediaItem[]>([]);
  const [topRatedShows, setTopRatedShows] = useState<MediaItem[]>([]);
  const [topRatedMovies, setTopRatedMovies] = useState<MediaItem[]>([]);
  const [upcoming, setUpcoming] = useState<MediaItem[]>([]);
  const [recentlyReleased, setRecentlyReleased] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [featuredContent, setFeaturedContent] = useState<MediaItem | null>(null);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [userLanguages, setUserLanguages] = useState<string[]>([]);
  const [seasonalPeriod, setSeasonalPeriod] = useState<SeasonalPeriod | null>(null);
  const [seasonalMovies, setSeasonalMovies] = useState<MediaItem[]>([]);

  useEffect(() => {
    // Fetch seasonal content first to avoid flickering
    const loadContent = async () => {
      const hasSeasonalContent = await fetchSeasonalContent();
      // Pass whether we have seasonal content to fetchContent
      await fetchContent(hasSeasonalContent);
      if (user) {
        loadUserLanguages();
      }
    };
    loadContent();
  }, [user]);

  const loadUserLanguages = async () => {
    if (!user) return;
    try {
      const languages = await detectUserLanguages(user.uid, watchlist);
      setUserLanguages(languages.primary.concat(languages.secondary));
    } catch (error) {
      setUserLanguages(['en']); // Default to English
    }
  };

  const fetchContent = async (hasSeasonalHero: boolean = false) => {
    try {
      const [trendingData, showsData, moviesData, topRatedShowsData, topRatedMoviesData, upcomingData, recentlyReleasedData] = await Promise.all([
        tmdb.getTrendingShows('day'),
        tmdb.getPopularShows(),
        tmdb.getPopularMovies(),
        tmdb.getTopRatedShows(),
        tmdb.getTopRatedMovies(),
        tmdb.getUpcomingMovies(),
        tmdb.getRecentlyReleasedMovies(),
      ]);

      const trendingItems = trendingData.results.slice(0, 21).map((item: any) => ({ ...item, media_type: 'tv' }));
      setTrending(trendingItems);
      setPopularShows(showsData.results.slice(0, 20));
      setPopularMovies(moviesData.results.slice(0, 20));
      setTopRatedShows(topRatedShowsData.results.slice(0, 20));
      setTopRatedMovies(topRatedMoviesData.results.slice(0, 20));

      // Upcoming movies now only contain future releases from the API
      setUpcoming(upcomingData.results.slice(0, 20));

      setRecentlyReleased(recentlyReleasedData.results.slice(0, 20));

      // Only set featured content from trending if no seasonal content exists
      if (!hasSeasonalHero && trendingItems.length > 0) {
        const itemsWithBackdrops = trendingItems.filter(item => item.backdrop_path && item.backdrop_path.trim() !== '');
        if (itemsWithBackdrops.length > 0) {
          setFeaturedContent(itemsWithBackdrops[0]);
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchSeasonalContent = async () => {
    try {
      // Check if there's an active seasonal period
      const activePeriod = await getActiveSeasonalPeriod();

      if (activePeriod) {
        setSeasonalPeriod(activePeriod);

        let seasonalItems: any[] = [];

        // Use custom movie list if available, otherwise fall back to keyword discovery
        if (activePeriod.movies && activePeriod.movies.length > 0) {
          // Use the custom movie list from admin panel
          seasonalItems = activePeriod.movies.map((movie) => ({
            ...movie,
            media_type: 'movie'
          }));
        } else if (activePeriod.keywordId) {
          // Fallback: Fetch movies with the seasonal keyword (deprecated)
          const seasonalData = await tmdb.discoverByKeyword(activePeriod.keywordId, 1);
          seasonalItems = seasonalData.results.slice(0, 20).map((item: any) => ({
            ...item,
            media_type: 'movie'
          }));
        }

        // Randomize movie order using daily seed for variety
        const themeSeed = activePeriod.movieSeed || 0;
        const dailySeed = getDailySeed(themeSeed);
        seasonalItems = shuffleWithSeed(seasonalItems, dailySeed);

        setSeasonalMovies(seasonalItems);

        // Set hero to rotate daily from seasonal movies
        if (seasonalItems.length > 0) {
          const itemsWithBackdrops = seasonalItems.filter(item => item.backdrop_path && item.backdrop_path.trim() !== '');
          if (itemsWithBackdrops.length > 0) {
            // Use day of year to rotate through seasonal movies
            const now = new Date();
            const start = new Date(now.getFullYear(), 0, 0);
            const diff = now.getTime() - start.getTime();
            const oneDay = 1000 * 60 * 60 * 24;
            const dayOfYear = Math.floor(diff / oneDay);

            // Pick different movie each day based on day of year
            const heroIndex = dayOfYear % itemsWithBackdrops.length;
            setFeaturedContent(itemsWithBackdrops[heroIndex]);
          }
        }
        return true; // Return true if seasonal content was set
      } else {
        setSeasonalPeriod(null);
        setSeasonalMovies([]);
        return false; // Return false if no seasonal content
      }
    } catch (error) {
      setSeasonalPeriod(null);
      setSeasonalMovies([]);
      return false;
    }
  };

  const loadMoreContent = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const [moreShows, moreMovies] = await Promise.all([
        tmdb.getPopularShows(nextPage),
        tmdb.getPopularMovies(nextPage)
      ]);

      if (moreShows.results.length === 0 && moreMovies.results.length === 0) {
        setHasMore(false);
      } else {
        // Deduplicate before adding new items
        setPopularShows(prev => {
          const combined = [...prev, ...moreShows.results];
          return combined.filter((item, index, self) =>
            index === self.findIndex(t => t.id === item.id)
          );
        });
        setPopularMovies(prev => {
          const combined = [...prev, ...moreMovies.results];
          return combined.filter((item, index, self) =>
            index === self.findIndex(t => t.id === item.id)
          );
        });
        setPage(nextPage);
      }
    } catch (error) {
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, page]);

  useEffect(() => {
    const handleScroll = () => {
      // Only trigger infinite scroll when actively searching
      if (searchQuery.length >= 2) {
        if (
          window.innerHeight + document.documentElement.scrollTop
          >= document.documentElement.offsetHeight - 1000
        ) {
          loadMoreContent();
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadMoreContent, searchQuery]);

  const handleSearch = async () => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const data = await tmdb.searchMulti(searchQuery);
      const filtered = data.results.filter((item: any) =>
        item.media_type === 'tv' || item.media_type === 'movie'
      );
      setSearchResults(filtered);
    } catch (error) {
    } finally {
      setSearchLoading(false);
    }
  };

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      handleSearch();
    }, 500);

    return () => clearTimeout(delaySearch);
  }, [searchQuery]);


  const scrollPositions = useRef<Record<string, number>>({});

  const MediaSection = memo(({
    title,
    items,
    icon: Icon,
    type,
    horizontal = false,
    sectionId,
    category
  }: {
    title: string;
    items: MediaItem[];
    icon: any;
    type?: string;
    horizontal?: boolean;
    sectionId?: string;
    category?: string;
  }) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const sectionKey = sectionId || title.replace(/\s+/g, '-').toLowerCase();

    // Restore scroll position after render
    useEffect(() => {
      if (scrollRef.current && scrollPositions.current[sectionKey] !== undefined) {
        scrollRef.current.scrollLeft = scrollPositions.current[sectionKey];
      }
    });

    // Save scroll position when scrolling
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
      scrollPositions.current[sectionKey] = e.currentTarget.scrollLeft;
    };

    // Filter out items without poster images for a professional look
    const itemsWithPosters = items.filter(item => item.poster_path && item.poster_path.trim() !== '');

    // Don't render section if no items have posters
    if (itemsWithPosters.length === 0) return null;

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon className="h-6 w-6 text-red-500" />
            <h2 className="text-2xl font-bold text-white">{title}</h2>
          </div>
          {horizontal && category && (
            <Button
              variant="ghost"
              size="sm"
              className="text-red-400 hover:text-red-300"
              onClick={() => router.push(`/browse/${category}`)}
            >
              View All
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
        {horizontal ? (
          <div className="overflow-x-auto" ref={scrollRef} onScroll={handleScroll}>
            <div className="flex gap-4 md:gap-6 pb-6" style={{ width: 'max-content' }}>
              {itemsWithPosters.slice(0, 20).map((item, index) => (
                <ReusableMediaCard
                  key={`${sectionId || title.replace(/\s+/g, '-').toLowerCase()}-${item.id}-${type || item.media_type || 'unknown'}-${index}`}
                  item={item}
                  mediaType={type}
                  showAddButton={true}
                  size="medium"
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
            {itemsWithPosters.map((item, index) => (
              <ReusableMediaCard key={`${sectionId || title.replace(/\s+/g, '-').toLowerCase()}-${item.id}-${type || item.media_type || 'unknown'}-${index}`} item={item} mediaType={type} showAddButton={true} size="small" />
            ))}
          </div>
        )}
      </div>
    );
  });

  const HeroSection = ({ item }: { item: MediaItem }) => {
    const mediaType = item.media_type || (item.first_air_date ? 'tv' : 'movie');
    const title = item.title || item.name;
    const year = (item.release_date || item.first_air_date) ? new Date(item.release_date || item.first_air_date!).getFullYear() : null;

    return (
      <div className="relative h-[65vh] min-h-[500px] md:h-[70vh] md:min-h-[550px] overflow-hidden">
        {item.backdrop_path && (
          <Image
            src={tmdb.getImageUrl(item.backdrop_path, 'original')}
            alt={title || ''}
            fill
            sizes="100vw"
            className="object-cover object-center md:object-cover"
            style={{ objectPosition: 'center 30%' }}
            priority
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 md:from-black/80 via-transparent to-transparent" />

        <div className="absolute bottom-0 left-0 right-0 p-4 md:p-8 lg:p-12 pt-20 md:pt-24">
          <div className="container mx-auto">
            <div className="max-w-2xl">
              <Badge className="bg-red-600/90 text-white border-0 text-xs md:text-sm mb-3 md:mb-4">
                {seasonalPeriod ? <Calendar className="h-3 w-3 mr-1 md:mr-2" /> : <Sparkles className="h-3 w-3 mr-1 md:mr-2" />}
                {seasonalPeriod ? `${seasonalPeriod.name.toUpperCase()} FEATURED` : (mediaType === 'tv' ? 'TRENDING TV SERIES' : 'TRENDING MOVIE')}
              </Badge>
              <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold text-white mb-3 md:mb-4 leading-tight">
                {title}
              </h1>
              {item.overview && (
                <p className="text-gray-200 text-sm md:text-lg mb-4 md:mb-6 line-clamp-2 md:line-clamp-3">
                  {item.overview}
                </p>
              )}
              <div className="flex items-center flex-wrap gap-3 md:gap-6 mb-6 md:mb-8 text-gray-300 text-sm md:text-base">
                {item.vote_average > 0 && (
                  <div className="flex items-center gap-2">
                    <Star className="h-4 w-4 md:h-5 md:w-5 fill-yellow-500 text-yellow-500" />
                    <span className="font-semibold">{item.vote_average.toFixed(1)}</span>
                  </div>
                )}
                {year && (
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 md:h-5 md:w-5" />
                    <span>{year}</span>
                  </div>
                )}
                <Badge variant="outline" className="border-gray-500 text-gray-300 text-xs md:text-sm">
                  {mediaType === 'tv' ? 'TV Series' : 'Movie'}
                </Badge>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 md:gap-4">
                <Button asChild size="sm" className="bg-red-600 hover:bg-red-700 text-white px-6 md:px-8 w-full sm:w-auto">
                  <Link href={`/${mediaType}/${item.id}`}>
                    <PlayCircle className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                    Watch Now
                  </Link>
                </Button>
                <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10 px-6 md:px-8 w-full sm:w-auto" asChild>
                  <Link href={`/${mediaType}/${item.id}`}>
                    <Info className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                    More Info
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Header with integrated search */}
      <Header showSearch={true} transparent={true} page="landing" />

      {/* Hero Section */}
      {!searchQuery && !loading && featuredContent && (
        <HeroSection item={featuredContent} />
      )}

      {/* Main Content */}
      <main className={!searchQuery && !loading && featuredContent ? "" : "pt-32 md:pt-24"}>
        {/* Search Results */}
        {searchQuery && (
          <div className="container mx-auto px-4 py-8">
            {searchLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-8 w-64" />
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-3 md:gap-4">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <Skeleton key={i} className="aspect-[2/3] rounded-xl" />
                  ))}
                </div>
              </div>
            ) : searchResults.length > 0 ? (
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <Search className="h-6 w-6 text-red-500" />
                  <h2 className="text-2xl font-bold text-white">Search Results for "{searchQuery}"</h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
                  {searchResults.filter(item => item.poster_path).map((item, index) => (
                    <ReusableMediaCard
                      key={`search-${item.id}-${item.media_type}-${index}`}
                      item={item}
                      mediaType={item.media_type as 'tv' | 'movie'}
                      showAddButton={true}
                      size="small"
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gray-800 flex items-center justify-center">
                  <Search className="h-10 w-10 text-gray-600" />
                </div>
                <p className="text-gray-400 text-lg">No results found for "{searchQuery}"</p>
                <p className="text-gray-500 text-sm mt-2">Try adjusting your search terms</p>
              </div>
            )}
          </div>
        )}

        {/* Content Tabs */}
        {!searchQuery && (
          <div className="bg-gradient-to-b from-transparent to-black py-8 md:py-12">
            <Tabs defaultValue="all" className="space-y-8 border-none outline-none">
              <div className="container mx-auto px-4">
                <div className="flex justify-center">
                  <div className="overflow-x-auto scrollbar-hide w-full" style={{ touchAction: 'pan-x pinch-zoom' }}>
                    <div className="flex justify-center min-w-max">
                      <TabsList className="bg-gray-900/80 border-2 border-gray-700/50 backdrop-blur-sm rounded-xl p-1 md:p-2 shadow-2xl" style={{ touchAction: 'manipulation' }}>
                        <TabsTrigger
                          value="all"
                          className="data-[state=active]:bg-red-600 data-[state=active]:text-white px-3 md:px-6 py-2 md:py-3 text-sm md:text-base font-semibold rounded-lg transition-all duration-300 hover:bg-white/10 whitespace-nowrap"
                        >
                          <Sparkles className="h-4 w-4 md:h-5 md:w-5 mr-1 md:mr-2" />
                          <span className="hidden sm:inline">All Content</span>
                          <span className="sm:hidden">All</span>
                        </TabsTrigger>
                        <TabsTrigger
                          value="trending"
                          className="data-[state=active]:bg-red-600 data-[state=active]:text-white px-3 md:px-6 py-2 md:py-3 text-sm md:text-base font-semibold rounded-lg transition-all duration-300 hover:bg-white/10 whitespace-nowrap"
                        >
                          <TrendingUp className="h-4 w-4 md:h-5 md:w-5 mr-1 md:mr-2" />
                          Trending
                        </TabsTrigger>
                        <TabsTrigger
                          value="shows"
                          className="data-[state=active]:bg-red-600 data-[state=active]:text-white px-3 md:px-6 py-2 md:py-3 text-sm md:text-base font-semibold rounded-lg transition-all duration-300 hover:bg-white/10 whitespace-nowrap"
                        >
                          <Tv className="h-4 w-4 md:h-5 md:w-5 mr-1 md:mr-2" />
                          <span className="hidden sm:inline">TV Shows</span>
                          <span className="sm:hidden">Shows</span>
                        </TabsTrigger>
                        <TabsTrigger
                          value="movies"
                          className="data-[state=active]:bg-red-600 data-[state=active]:text-white px-3 md:px-6 py-2 md:py-3 text-sm md:text-base font-semibold rounded-lg transition-all duration-300 hover:bg-white/10 whitespace-nowrap"
                        >
                          <Film className="h-4 w-4 md:h-5 md:w-5 mr-1 md:mr-2" />
                          Movies
                        </TabsTrigger>
                      </TabsList>
                    </div>
                  </div>
                </div>
              </div>

            <TabsContent value="all" className="space-y-12 container mx-auto px-4 border-none outline-none">
              {loading ? (
                <div className="space-y-8">
                  {Array.from({ length: 4 }).map((_, sectionIndex) => (
                    <div key={sectionIndex} className="space-y-4">
                      <Skeleton className="h-8 w-64" />
                      <div className="overflow-x-auto scrollbar-hide">
                        <div className="flex gap-6 pb-6" style={{ width: 'max-content' }}>
                          {Array.from({ length: 8 }).map((_, i) => (
                            <Skeleton key={i} className="w-48 aspect-[2/3] rounded-xl flex-shrink-0" />
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  {/* Category Browser - Browse by Genre */}
                  <CategoryBrowser />

                  {/* Seasonal Movies - Show first when active */}
                  {seasonalPeriod && seasonalMovies.length > 0 && (
                    <MediaSection
                      title={`${seasonalPeriod.name} Movies`}
                      items={seasonalMovies}
                      icon={Calendar}
                      type="movie"
                      horizontal={true}
                      sectionId={`seasonal-${seasonalPeriod.id}`}
                      category={`seasonal/${seasonalPeriod.id}`}
                    />
                  )}

                  {/* Personalized Recommendations - Only show if user is signed in */}
                  {user && (
                    <>
                      <PersonalizedRow
                        title="Recommended For You"
                        category="personalized"
                        description="Based on your watchlist and viewing preferences"
                        className="mb-8"
                      />

                      {userLanguages.length > 1 && (
                        <PersonalizedRow
                          title={`Popular in ${userLanguages[0] === 'en' ? 'English' : userLanguages[0].toUpperCase()}`}
                          category="language_specific"
                          language={userLanguages[0]}
                          description="Content in your preferred language"
                          className="mb-8"
                        />
                      )}

                      <PersonalizedRow
                        title="Continue Watching"
                        category="continue"
                        description="Pick up where you left off"
                        className="mb-8"
                      />

                      <PersonalizedRow
                        title="Watch Again"
                        category="watch_again"
                        description="Revisit your favorites"
                        className="mb-8"
                      />

                      <PersonalizedRow
                        title="Trending Now"
                        category="trending"
                        description="What everyone's watching today"
                        className="mb-8"
                      />
                    </>
                  )}

                  {/* Fallback to regular MediaSection if not signed in */}
                  {!user && (
                    <MediaSection
                      title="Trending Now"
                      items={trending.slice(1)}
                      icon={TrendingUp}
                      horizontal={true}
                      sectionId="all-trending"
                      category="trending"
                    />
                  )}
                  <MediaSection
                    title="Popular TV Shows"
                    items={popularShows}
                    icon={Tv}
                    type="tv"
                    horizontal={true}
                    sectionId="all-popular-tv"
                    category="popular-shows"
                  />
                  <MediaSection
                    title="Popular Movies"
                    items={popularMovies}
                    icon={Film}
                    type="movie"
                    horizontal={true}
                    sectionId="all-popular-movies"
                    category="popular-movies"
                  />
                  <MediaSection
                    title="Top Rated TV Shows"
                    items={topRatedShows}
                    icon={Star}
                    type="tv"
                    horizontal={true}
                    sectionId="all-top-rated-tv"
                    category="top-rated-shows"
                  />
                  <MediaSection
                    title="Top Rated Movies"
                    items={topRatedMovies}
                    icon={Star}
                    type="movie"
                    horizontal={true}
                    sectionId="all-top-rated-movies"
                    category="top-rated-movies"
                  />
                  <MediaSection
                    title="Recently Released"
                    items={recentlyReleased}
                    icon={Clock}
                    type="movie"
                    horizontal={true}
                    sectionId="recently-released-movies"
                    category="recently-released"
                  />
                  <MediaSection
                    title="Coming Soon"
                    items={upcoming}
                    icon={Calendar}
                    type="movie"
                    horizontal={true}
                    sectionId="all-upcoming-movies"
                    category="upcoming"
                  />
                  {loadingMore && searchQuery.length >= 2 && (
                    <div className="flex justify-center py-8">
                      <div className="flex items-center gap-2 text-gray-400">
                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-red-500 border-t-transparent"></div>
                        <span>Loading more content...</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </TabsContent>

            <TabsContent value="trending" className="container mx-auto px-4 border-none outline-none">
              <MediaSection
                title="Trending Now"
                items={trending}
                icon={TrendingUp}
                horizontal={true}
                sectionId="trending-tab"
              />
            </TabsContent>

            <TabsContent value="shows" className="container mx-auto px-4 border-none outline-none">
              <MediaSection
                title="Popular TV Shows"
                items={popularShows}
                icon={Tv}
                type="tv"
                horizontal={true}
                sectionId="shows-tab"
              />
            </TabsContent>

            <TabsContent value="movies" className="container mx-auto px-4 border-none outline-none">
              <MediaSection
                title="Popular Movies"
                items={popularMovies}
                icon={Film}
                type="movie"
                horizontal={true}
                sectionId="movies-tab"
              />
            </TabsContent>
            </Tabs>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800 mt-16 bg-gradient-to-b from-gray-950 to-black">
        <div className="container mx-auto px-4 py-12">
          <div className="text-center">
            <div className="mb-6">
              <div className="mb-4">
                <Image
                  src={themeLoading ? "/image/epify.png" : (activeTheme?.logo?.url || "/image/epify.png")}
                  alt="Epify - Ultimate Entertainment Tracker"
                  width={200}
                  height={60}
                  className="mx-auto h-12 w-auto"
                  priority
                />
              </div>
              <p className="text-gray-400 max-w-md mx-auto">
                Your ultimate companion for tracking movies and TV shows. Never miss a new season or episode again.
              </p>
            </div>
            <div className="flex justify-center gap-8 mb-8">
              <div className="text-center">
                <div className="text-2xl font-bold text-white">10K+</div>
                <div className="text-sm text-gray-500">Movies & Shows</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white">24/7</div>
                <div className="text-sm text-gray-500">Updates</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white">Free</div>
                <div className="text-sm text-gray-500">Forever</div>
              </div>
            </div>
            <div className="text-gray-500 text-sm space-y-3">
              <div className="flex items-center justify-center gap-2">
                <span>Powered by</span>
                <Image
                  src="/image/TMDB.svg"
                  alt="The Movie Database"
                  width={80}
                  height={16}
                  className="h-4 w-auto"
                />
              </div>
              <p className="text-xs">© 2024 Epify. Built for entertainment enthusiasts.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}